import { logger } from "@/services/Logger";

// Simple, reliable audio for notifications
let notificationAudio: HTMLAudioElement | null = null;

const getNotificationAudio = (): HTMLAudioElement => {
  if (!notificationAudio) {
    notificationAudio = new Audio("/notification.wav");
    notificationAudio.load();
  }
  return notificationAudio;
};

/**
 * Unlocks the notification sound. Browsers (notably Safari/iOS) only let an
 * audio element play after it has been started from a user gesture, so call
 * this from a tap or key press: it plays the shared element muted, then
 * rewinds it, so later notifications can play without a gesture.
 * @returns Promise that resolves to true if the element could be started
 */
export const primeNotificationAudio = async (): Promise<boolean> => {
  const audio = getNotificationAudio();
  try {
    audio.muted = true;
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    return true;
  } catch (error) {
    logger.warn("audioUtils", "Could not unlock notification sound:", error);
    return false;
  } finally {
    audio.muted = false;
  }
};

/**
 * Plays the notification sound once
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const playNotificationSound = async (): Promise<boolean> => {
  try {
    const audio = getNotificationAudio();

    // Reset to beginning in case it was played before
    audio.currentTime = 0;

    // Set volume
    audio.volume = 1.0;

    // Try playing
    await audio.play();
    logger.info(
      "audioUtils",
      "✅ Notification sound played successfully (Method 1)"
    );
    return true;
  } catch (error) {
    logger.error("audioUtils", "Error playing notification sound:", error);

    // Fallback: Create a new Audio instance and try again
    try {
      const fallbackAudio = new Audio("/notification.wav");
      await fallbackAudio.play();
      logger.info(
        "audioUtils",
        "✅ Notification sound played successfully (Method 2)"
      );
      return true;
    } catch (fallbackError) {
      logger.error("audioUtils", "All audio playback methods failed:", fallbackError);
      return false;
    }
  }
};
